/**
 * Tenants list: state, gallery space, total records, users count.
 * Create tenant button; row click opens detail.
 */

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Chip,
  CircularProgress,
  Alert,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import { tenantService } from '../services/api';

/** Format bytes to human-readable (e.g. 1.5 MB) */
function formatBytes(bytes) {
  if (bytes == null || bytes === 0) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(2))} ${sizes[i]}`;
}

export default function Tenants() {
  const navigate = useNavigate();
  const [tenants, setTenants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    load();
  }, []);

  const load = async () => {
    setLoading(true);
    setError('');
    try {
      const res = await tenantService.list();
      setTenants(res.data || []);
    } catch (err) {
      setError(err.message || 'Failed to load tenants');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight={200}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h5">Tenants (Companies)</Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => navigate('/tenants/new', { replace: false })}
        >
          Add Tenant
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Company Code</TableCell>
              <TableCell>Name</TableCell>
              <TableCell>State</TableCell>
              <TableCell>Users</TableCell>
              <TableCell>Installations</TableCell>
              <TableCell>Teams</TableCell>
              <TableCell>Gallery Space</TableCell>
              <TableCell>Status</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {tenants.length === 0 ? (
              <TableRow>
                <TableCell colSpan={8} align="center">
                  No tenants yet. Create one to get started.
                </TableCell>
              </TableRow>
            ) : (
              tenants.map((t) => (
                <TableRow
                  key={t._id}
                  hover
                  sx={{ cursor: 'pointer' }}
                  onClick={() => navigate(`/tenants/${t._id}`)}
                >
                  <TableCell>
                    <Typography fontWeight="600">{t.company_code}</Typography>
                  </TableCell>
                  <TableCell>{t.name}</TableCell>
                  <TableCell>
                    <Chip
                      label={t.state || 'active'}
                      size="small"
                      color={t.state === 'suspended' ? 'warning' : 'success'}
                      variant="outlined"
                    />
                  </TableCell>
                  <TableCell>{t.users_count ?? 0}</TableCell>
                  <TableCell>{t.installations_count ?? 0}</TableCell>
                  <TableCell>{t.teams_count ?? 0}</TableCell>
                  <TableCell>
                    {formatBytes(t.gallery_space_used ?? 0)}
                    {(t.gallery_space_limit ?? 0) > 0 && (
                      <> / {formatBytes(t.gallery_space_limit)}</>
                    )}
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={t.is_active ? 'Active' : 'Inactive'}
                      size="small"
                      color={t.is_active ? 'primary' : 'default'}
                      variant="outlined"
                    />
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
}
